import { EPaletteModes } from '@/types/theme-packs.types';
import {
  parseColor,
  resolveColor,
  eventImage,
  getHeaderImage,
  getBtvFallbackForBg,
  formatDateParts,
} from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const Pink = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  mode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = mode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[mode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const accent = resolveColor(themeColors.accentColor, customizations?.accentColor);
  const originalHeading = customizations?.headingTextColor || parseColor(themeColors.headingTextColor);
  const originalButtonTextColor =
    customizations?.buttonTextColor || parseColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor);
  const originalBody = customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor);
  const heading = getRelativeContrastColor(originalHeading, accent);
  const body = getRelativeContrastColor(originalBody, container);
  const buttonTextColor = getRelativeContrastColor(originalButtonTextColor, heading);

  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionParts = event.sessions[0]?.timestamp ? formatDateParts(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(accent);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont ? `${customizations.headingFont}, sans-serif` : 'Georgia, serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Trebuchet MS, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || 'You have been invited to');
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Pink"
      width={520}
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--body': body,
          '--heading': heading,
          '--button-text': buttonTextColor,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
          width: '100%',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)' }}
            >
              <tbody>
                <tr style={{ background: 'var(--accent)' }}>
                  <td align="center" valign="middle" height={48} style={{ padding: '8px' }}>
                    {headerImg && (
                      <div
                        onClick={uploadHeaderImg}
                        data-cont="img"
                        className={loadingHeaderImg ? styles.imgLoading : undefined}
                      >
                        <img height={32} src={headerImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ background: 'var(--accent)', padding: '16px 8px 8px 8px' }}>
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '14px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 700,
                        lineHeight: '11.2px',
                        letterSpacing: '0.42px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ background: 'var(--accent)', padding: '8px 8px 24px 8px' }}>
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '48px',
                        fontFamily: 'var(--heading-font)',
                        fontStyle: 'italic',
                        fontWeight: 400,
                        textTransform: 'uppercase',
                        lineHeight: '48px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                        maxWidth: '492px',
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td
                    align="center"
                    style={{ background: 'var(--accent)', padding: '0 0 24px 0', fontSize: 0, lineHeight: 0 }}
                  >
                    {bodyImg && (
                      <div onClick={uploadMain} data-cont="img" className={loadingMain ? styles.imgLoading : undefined}>
                        <img width={348} src={bodyImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ height: '32px', fontSize: 0, lineHeight: 0 }}>&nbsp;</td>
                </tr>
                {eventDescription && (
                  <tr>
                    <td align="center" style={{ padding: '0 40px 24px 40px' }}>
                      <ContentEditable
                        value={eventDescription}
                        setValue={setDescription}
                        style={{
                          color: 'var(--body)',
                          fontSize: '14px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 400,
                          lineHeight: '16.8px',
                          wordWrap: 'break-word',
                          textAlign: 'center',
                          maxWidth: '317px',
                        }}
                      />
                    </td>
                  </tr>
                )}
                {/* Date boxes */}
                {sessionParts && (
                  <tr>
                    <td align="center" style={{ padding: '0 24px 24px 24px' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td
                              align="center"
                              style={{
                                border: '1px solid var(--body)',
                                padding: '16px 24px',
                                backgroundColor: 'var(--container)',
                                borderRadius: 25,
                                filter: 'brightness(1.6) saturate(0.5)',
                              }}
                            >
                              <div style={{ textAlign: 'center', width: '98px' }}>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    lineHeight: '14px',
                                    letterSpacing: '0.42px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.dayName}
                                </div>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '32px',
                                    fontFamily: 'var(--heading-font)',
                                    fontWeight: 400,
                                    textTransform: 'uppercase',
                                    lineHeight: '32px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.shortDate}
                                </div>
                              </div>
                            </td>
                            <td width={20}>&nbsp;</td>
                            <td
                              align="center"
                              style={{
                                border: '1px solid var(--body)',
                                padding: '16px 24px',
                                backgroundColor: 'var(--container)',
                                borderRadius: 25,
                                filter: 'brightness(1.6) saturate(0.5)',
                              }}
                            >
                              <div style={{ textAlign: 'center' }}>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    lineHeight: '14px',
                                    letterSpacing: '0.42px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.period}
                                </div>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '32px',
                                    fontFamily: 'var(--heading-font)',
                                    fontWeight: 400,
                                    textTransform: 'uppercase',
                                    lineHeight: '32px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.time}
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                <tr>
                  <td align="center" style={{ paddingBottom: '40px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{ background: 'var(--heading)', width: '148px', height: '40px' }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()}
                              style={{
                                textDecoration: 'none',
                                padding: '6px 0 6px 0',
                                width: '148px',
                                height: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '13px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  lineHeight: '16px',
                                  letterSpacing: '0.39px',
                                  wordWrap: 'break-word',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 700,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 700,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 700,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
